import React, { useState, useCallback } from 'react';
import { Plus, GripVertical, AlertCircle } from 'lucide-react';
import { DEALS, PIPELINE_STAGES } from '../constants';
import { Deal } from '../types';

const Pipeline: React.FC = () => {
  const [deals, setDeals] = useState<Deal[]>(DEALS);
  const [draggedDealId, setDraggedDealId] = useState<string | null>(null);
  const [lastWonId, setLastWonId] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedDealId(id);
    e.dataTransfer.effectAllowed = "move";
    // Invisible drag image or customize if needed, browser default is okay for simplicity
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = useCallback((e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    if (!draggedDealId) return;

    setDeals((prev) => 
      prev.map(d => d.id === draggedDealId ? { ...d, stageId } : d)
    );

    if (stageId === 'won') {
        setLastWonId(draggedDealId);
        // Reset animation trigger after play
        setTimeout(() => setLastWonId(null), 2000); 
    }

    setDraggedDealId(null);
  }, [draggedDealId]);

  const getDealsByStage = (stageId: string) => deals.filter(d => d.stageId === stageId);

  const calculateTotal = (stageId: string) => {
      const stageDeals = getDealsByStage(stageId);
      return stageDeals.reduce((sum, d) => sum + d.value, 0).toLocaleString();
  };

  return (
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">
        <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium text-slate-800">Sales Pipeline</h2>
            <div className="flex space-x-2">
                 <span className="text-xs font-medium text-slate-500 bg-white/50 px-3 py-1.5 rounded-full border border-white/40">
                    Total Pipeline: ${(deals.reduce((a,b)=>a+b.value,0)).toLocaleString()}
                 </span>
            </div>
        </div>

      <div className="flex-1 overflow-x-auto overflow-y-hidden pb-4">
        <div className="flex h-full space-x-4 min-w-[1000px]">
          {PIPELINE_STAGES.map((stage) => (
            <div 
                key={stage.id} 
                className="flex-1 flex flex-col min-w-[200px] h-full"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
            >
              {/* Column Header */}
              <div className="flex justify-between items-center mb-3 px-1">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${stage.color.replace('bg-', 'bg-').replace('200', '500')}`}></div>
                  <span className="text-sm font-medium text-slate-700">{stage.name}</span>
                  <span className="text-xs text-slate-400 font-light">{getDealsByStage(stage.id).length}</span>
                </div>
                {getDealsByStage(stage.id).length > 0 && (
                    <span className="text-[10px] text-slate-400 font-mono">${calculateTotal(stage.id)}</span>
                )}
              </div>

              {/* Column Body */}
              <div className={`flex-1 bg-slate-100/30 rounded-xl p-2 space-y-3 overflow-y-auto transition-colors duration-200 ${draggedDealId ? 'bg-slate-100/60' : ''}`}>
                {getDealsByStage(stage.id).map((deal) => (
                  <div
                    key={deal.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, deal.id)}
                    className={`
                        bg-white/80 backdrop-blur-sm p-3 rounded-lg border border-white/60 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-all
                        ${lastWonId === deal.id ? 'animate-pulse ring-2 ring-slate-400' : ''}
                    `}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-medium text-slate-500 truncate max-w-[100px]">{deal.clientName}</span>
                      <GripVertical size={12} className="text-slate-300" />
                    </div>
                    <h4 className="text-sm font-semibold text-slate-800 mb-3 leading-tight">{deal.title}</h4>
                    <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                      <span className="text-xs font-mono text-slate-600 font-medium">${deal.value.toLocaleString()}</span>
                      <div className="flex items-center space-x-2">
                        {deal.daysOpen > 10 && (
                            <span className="flex items-center text-[10px] text-amber-500 bg-amber-50 px-1 rounded">
                                <AlertCircle size={8} className="mr-0.5" />
                                {deal.daysOpen}d
                            </span>
                        )}
                        <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-bold text-slate-600">
                            {deal.ownerInitial}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Add Button Placeholder */}
                <button className="w-full py-2 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 rounded-lg transition-colors border border-dashed border-slate-300/50">
                    <Plus size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Pipeline;